import React, { useState, useEffect } from "react";
import { listMetrics, updateMetric, getAllBenchmarks } from "../../../services/MetricService";
import { AggregationType, ColumnType, FilterOperator, MetricEdgeType, SortByEnum } from "build-ts/enums";
import CreateMKG from "../../../components/configuration/snowflake/CreateMKG";
import { getAllKGColumns, getColumnValues, getDataModelHeaders, getModelById } from "../../../services/DataModelService";
import showToast from "../../../hooks/useCustomToast";
import { 
  Header, 
  ColumnInfo, 
  FilterProto, 
  CorrelationProto, 
  FundamentalRelationshipProto, 
  MetricProto, 
  MetricEdgeProto,
  ObservationColumnProto,
  ModelInfo,
  ObservationContentProto,
} from "build-ts/metadata";
import { CreateOrUpdateMetrics_Request } from "build-ts/interface";
import { DateFilter } from "build-ts/relative_date";
import { DateFilterCategory } from "utils/enum";

interface CreateMKGContainerProps {
  metricInfo: CreateOrUpdateMetrics_Request | null; // Details of the existing metric (if any)
  modelInfo: ModelInfo | null; // Array of available columns for related attributes
}

const CreateMKGContainer: React.FC<CreateMKGContainerProps> = ({
  metricInfo,
  modelInfo,
}) => {

  const [measureColumn, setMesureColumn] = useState<Header[] | null>(null);
  const [attributeColumn, setAttributeColumn] = useState<Header[] | null>(null);
  const [allMetrics, setAllMetrics] = useState<Header[] | null>(null);
  const [allBenchmarks, setAllBenchmarks] = useState<Header[] | null>(null);
  const [allKGColumns, setAllKGColumns] = useState<Header[] | null>(null);
  const [selectedBenchmark, setSelectedBenchmark] = useState<Header | null>(null);
  const [selectedMetricHeader, setSelectedMetricHeader] = useState<Header | null>(null);

  //related attribute
  const [relatedAttributeEdge, setRelatedAttributeEdge] = useState<
    MetricEdgeProto[] | null
  >(null);
  const [selectedRelatedAttributeColumn, setSelectedRelatedAttributeColumn] =
    useState<Header | null>(null);
  const [relatedAttributeEdgeWeight, setRelatedAttributeEdgeWeight] = useState<number>(0.2); // Default weight set to 0.2

  //benchmark
  const [benchmarkEdge, setBenchmarkEdge] = useState<MetricEdgeProto[]>([]);
  const [benchmarkFilter, setBenchmarkFilter] = useState<FilterProto[] | null>(null);
  const [benchmarkName, setBenchmarkName] = useState<string>("");
  const [benchmarkFilterValue, setBenchmarkFilterValue] =
    useState<string>("");
  const [selectedBenchmarkFilterColumn, setSelectedBenchmarkFilterColumn] =
    useState<ColumnInfo | null>(null);
  const [benchmarkEdgeWeight, setBenchmarkEdgeWeight] = useState<number>(0);
  const [columnNames, setColumnNames] = useState<string[]>([]);
  const [selectedColumnValues, setSelectedColumnValues] = useState<string[]>([]);

  //fundamental relationship
  const [fundamentalEdgeName, setFundamentalEdgeName] = useState<string>("");
  const [fundamentalEdgeWeight, setFundamentalEdgeWeight] = useState<number>(0);
  const [selectedType, setSelectedType] = useState<string>("Column");
  const [selectedHeader, setSelectedHeader] = useState<string>("");
  const [correlationFactor, setCorrelationFactor] = useState<string | "">("");
  const [correlations, setCorrelations] = useState<CorrelationProto[]>([]);
  const [fundamentalEdges, setFundamentalEdges] = useState<MetricEdgeProto[]>([]);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false);
  const [isCorrelationMenuOpen, setIsCorrelationMenuOpen] = useState<boolean>(false);
  const [isAdditive, setIsAdditive] = useState<boolean>(false);

  // States for Benchmark definition
  const [selectedObservationColumn, setSelectedObservationColumn] =
    useState<Header | null>(null);
  const [selectedFilterColumn, setSelectedFilterColumn] =
    useState<ColumnInfo | null>(null);
  const [selectedAggregation, setSelectedAggregation] = useState<AggregationType>();
  const [selectedOperator, setSelectedOperator] = useState<FilterOperator>();
  const [selectedSorting, setSelectedSorting] = useState<SortByEnum>();
  const [inputValue, setInputValue] = useState<string>("");
  const [selectedObservationColumns, setSelectedObservationColumns] = useState<
    ObservationColumnProto[]>([]);
  const [selectedFilterColumns, setSelectedFilterColumns] = useState<FilterProto[]>([]);
  const [targetDataModelHeaders, setTargetDataModelHeaders] = useState<Header[]>([]);
  const [selectedTargetModelId, setSelectedTargetModelId] = useState<string>(""); 
  const [targetModelInfo, setTargetModelInfo] = useState<ModelInfo | undefined>(); 
  const [benchmarkMode, setBenchmarkMode] = useState<"currentModel" | "targetModel">("currentModel");
  const [dateFilter, setDateFilter] = useState<DateFilter | undefined>(undefined);
  const [dateFilterCategory, setDateFilterCategory] = useState<DateFilterCategory>(DateFilterCategory.FIXED);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // applying filters to extract Measure and attribute columns
  useEffect(() => {
    if (modelInfo?.columns) {
      const measureColumns = modelInfo.columns?.filter(
        (column) => column.column_type === ColumnType.MEASURE
      );
      const attributeColumns = modelInfo.columns?.filter(
        (column) => column.column_type === ColumnType.ATTRIBUTE
      );
      setMesureColumn(measureColumns.map((column) => column.header!));
      setAttributeColumn(attributeColumns.map((column) => column.header!));
    }
    if (metricInfo?.metrics[0]?.definition) {
      const { filters, observation_columns } = metricInfo.metrics[0].definition;

      // Remove matching items from attributeColumns based on filters
      if (filters) {
        const filterIds = filters.map(
          (filter) => filter?.logical_column_header?.id
        );
        setAttributeColumn(
          (prevAttributeColumns) =>
            prevAttributeColumns &&
            prevAttributeColumns.filter(
              (column) => !filterIds.includes(column.id)
            )
        );
      }

      // Remove matching items from attributeColumns based on observation_columns
      if (observation_columns) {
        const observationColumnIds = observation_columns.map(
          (column) => column?.logical_column_header?.id
        );
        setAttributeColumn(
          (prevAttributeColumns) =>
            prevAttributeColumns &&
            prevAttributeColumns.filter(
              (column) => !observationColumnIds.includes(column.id)
            )
        );
      }
    }
    const fetchAllKGColumns = async () => {
      const kgColumns = await getAllKGColumns();
      setAllKGColumns(kgColumns);
    };
    fetchAllKGColumns();
  }, [modelInfo?.columns, metricInfo]);
  
  //this will load the state while edit MKG(like if there was edges before then it will load and then it made editable)
  useEffect(() => {
    if (metricInfo && metricInfo.metrics[0] && metricInfo.metrics[0].edges) {
      const attributeEdges: MetricEdgeProto[] = metricInfo.metrics[0].edges
        .filter((edge) => edge.edge_type === MetricEdgeType.ATTRIBUTE_EDGE || edge.edge_type === MetricEdgeType.METRIC_ATTRIBUTE_EDGE || 
          edge.edge_type === MetricEdgeType.METRIC_TIMEFRAME_EDGE || edge.edge_type === MetricEdgeType.METRIC_MEASURE_EDGE)
        .map((edge) => ({
          edge_type: edge.edge_type,
          edge_weight: edge.edge_weight!,
          header: edge.header!,
          source_metric_id: edge.source_metric_id
        }));
      setRelatedAttributeEdge(attributeEdges);
    }
    if (metricInfo && metricInfo.metrics[0] && metricInfo.metrics[0].edges) {
      const benchmarkEdges: MetricEdgeProto[] = metricInfo.metrics[0].edges
        .filter((edge) => edge.edge_type === MetricEdgeType.BENCHMARK_EDGE || edge.edge_type === MetricEdgeType.METRIC_BENCHMARK_EDGE)
        .map((edge) => ({
          edge_type: edge.edge_type,
          edge_weight: edge.edge_weight,
          benchmark: edge?.benchmark,
          source_metric_id: edge?.source_metric_id,
          header: edge?.header
        }));
      setBenchmarkEdge(benchmarkEdges);
    }
    if (metricInfo && metricInfo.metrics[0] && metricInfo.metrics[0].edges) {
      const fundaEdge: MetricEdgeProto[] = metricInfo.metrics[0].edges
        .filter((edge) => edge.edge_type === MetricEdgeType.METRIC_METRIC_EDGE)
        .map((edge) => ({
          edge_weight: edge.edge_weight,
          edge_type: edge.edge_type,
          header: edge.header,
          source_metric_id: edge.source_metric_id
        }));
      setFundamentalEdges(fundaEdge);
    }
  }, [metricInfo]);

  //fetch all metric
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await listMetrics(""/* modelId */);
        setAllMetrics(data);
      } catch (error) {
        showToast("Error fetching data", "", "error");
      }
    };
    const getTargetModels = async() => {
      const response = await getDataModelHeaders(false /* Include only target models */);
      setTargetDataModelHeaders(response);
    };
    const fetchBenchmarks = async () => {
      try {
        const benchmarks = await getAllBenchmarks();
        setAllBenchmarks(benchmarks);
      } catch (error) {
        showToast("Error fetching benchmarks", "", "error");
      }
    };
    getTargetModels();
    fetchData();
    fetchBenchmarks();
  }, []);

  // Whenever user selects a target model from the <Select>, fetch the full model
  const handleTargetModelSelect = async (id: string) => {
    setSelectedTargetModelId(id);
    if (id) {
      try {
        const targetModelInfo = await getModelById(id);
        if (targetModelInfo) {
          setTargetModelInfo(targetModelInfo);
        }
      } catch (err) {
        showToast("Error fetching target model info", "", "error");
      }
    } else {
      // If user resets the select to empty, also reset local state
      setTargetModelInfo(undefined);
    }
  };

  const toggleCorrelationMenu = () => setIsCorrelationMenuOpen(!isCorrelationMenuOpen);
  const toggleFilterMenu = () => setIsFilterMenuOpen(!isFilterMenuOpen);
  const handleAddRelatedAttributeEdge = () => {
    if (selectedRelatedAttributeColumn) {
      console.log("selectedRelatedAttributeColumn", Header.toJSON(selectedRelatedAttributeColumn));
      let newEdge: MetricEdgeProto;
      if (selectedRelatedAttributeColumn.column_type === ColumnType.ATTRIBUTE) {
        newEdge = MetricEdgeProto.create({
          edge_type: MetricEdgeType.METRIC_ATTRIBUTE_EDGE,
          edge_weight: relatedAttributeEdgeWeight || 0.2,
          header: selectedRelatedAttributeColumn,
          source_metric_id: metricInfo?.metrics[0]?.header?.id
        });
      } else if (selectedRelatedAttributeColumn.column_type === ColumnType.MEASURE) {
        newEdge = MetricEdgeProto.create({
          edge_type: MetricEdgeType.METRIC_MEASURE_EDGE,
          edge_weight: relatedAttributeEdgeWeight || 0.2,
          header: selectedRelatedAttributeColumn,
          source_metric_id: metricInfo?.metrics[0]?.header?.id
        });
      } else if (selectedRelatedAttributeColumn.column_type === ColumnType.TEMPORAL) {
        newEdge = MetricEdgeProto.create({
          edge_type: MetricEdgeType.METRIC_TIMEFRAME_EDGE,
          edge_weight: relatedAttributeEdgeWeight || 0.2,
          header: selectedRelatedAttributeColumn,
          source_metric_id: metricInfo?.metrics[0]?.header?.id
        });
      }
      
      // Retain previous edges and add the new edge
      setRelatedAttributeEdge((prev) =>
        prev ? [...prev, newEdge] : [newEdge]
      );
      setSelectedRelatedAttributeColumn(null);
      setRelatedAttributeEdgeWeight(0.5); // Reset to default 0.2
    }
  };
  const handleRemoveRelatedAttribute = (id: string) => {
    setRelatedAttributeEdge(
      (prev) =>
        prev && prev.filter((option) => option?.header?.id !== id)
    );
  };
  const handleAddBenchmarkFilter = () => {
    if (benchmarkName && (benchmarkFilterValue || selectedColumnValues)) {
      const newFilter = FilterProto.create({
        logical_column_header: selectedBenchmarkFilterColumn?.header!,
        operator: FilterOperator.EQ,
        values: selectedColumnValues.length > 0? selectedColumnValues: [benchmarkFilterValue],
      });
      setBenchmarkFilter((prevFilters) =>
        prevFilters ? [...prevFilters, newFilter] : [newFilter]
      );
      setSelectedBenchmarkFilterColumn(null);
      setSelectedColumnValues([]);
      setBenchmarkFilterValue("");
    } else {
      showToast("Please fill all the values.", "", "error");
    }
  };
  const handleAddBenchmark = () => {
    if (selectedBenchmark) {
      const bench = MetricEdgeProto.create({
        edge_weight: benchmarkEdgeWeight? benchmarkEdgeWeight : 0.5,
        source_metric_id: metricInfo?.metrics[0]?.header?.id,
        header: selectedBenchmark,
        edge_type: MetricEdgeType.METRIC_BENCHMARK_EDGE
      });
      setBenchmarkEdge((prevForms) => [...prevForms, bench]);
    }
    if (benchmarkName && benchmarkEdgeWeight !== undefined) {
      const benchmarkDefinition : ObservationContentProto = {
        observation_columns: selectedObservationColumns,
        filters: selectedFilterColumns
      };
      const newBenchmarkEdge: MetricEdgeProto = {
        edge_weight: benchmarkEdgeWeight,
        benchmark: {
          header: { name: benchmarkName },
          benchmark_filters: benchmarkFilter ?? [],
          growth_benchmarks: [],
          ...(benchmarkMode === "targetModel" && { definition: benchmarkDefinition }),
        },
      };
      
      setBenchmarkEdge((prevForms) => [...prevForms, newBenchmarkEdge]);

      setBenchmarkName("");
      setBenchmarkEdgeWeight(0);
      setBenchmarkFilter([]);
      setSelectedColumnValues([]);
      setBenchmarkFilterValue("");
      setSelectedTargetModelId("");
      setSelectedFilterColumns([]);
      setSelectedObservationColumns([]);
    }
  };
  const handleRemoveBenchmarkFilter = (id: String) => {
    setBenchmarkFilter(
      (prevFilters) =>
        prevFilters &&
        prevFilters.filter((filter) => filter?.logical_column_header?.id !== id)
    );
  };
  const handleRemoveBenchmarkEdge = (index: number) => {
    const updatedBenchmarkForms = [...benchmarkEdge];
    updatedBenchmarkForms.splice(index, 1);
    setBenchmarkEdge(updatedBenchmarkForms);
  };
  const handleAddCorrelation = () => {
    const headerList = selectedType === "Column" ? measureColumn : allMetrics;
    const headerToAdd = headerList?.find(
      (header) => header.name === selectedHeader
    );
    if (headerToAdd) {
      const newCorrelation: CorrelationProto = {
        correlation_factor: parseFloat(correlationFactor),
        ...(selectedType === "Column"
          ? { related_column_header: headerToAdd }
          : { related_metric_header: headerToAdd }),
      };
      setCorrelations([...correlations, newCorrelation]);
      setCorrelationFactor("");
    }
  };
  const handleRemoveCorrelation = (index: number) => {
    setCorrelations(correlations.filter((_, i) => i !== index));
  };
  const handleAddFundamentalEdges = () => {
    if (selectedMetricHeader) {
      const relatedMetricEdge = MetricEdgeProto.create({
        edge_weight: 0.5,
        edge_type: MetricEdgeType.METRIC_METRIC_EDGE,
        header: selectedMetricHeader,
        source_metric_id: metricInfo?.metrics[0]?.header?.id
      });
      setFundamentalEdges((prev) =>
        prev ? [...prev, relatedMetricEdge] : [relatedMetricEdge]
      );
    }
    if (fundamentalEdgeName && fundamentalEdgeWeight && correlations) {
      const newFundamentalRelationship: FundamentalRelationshipProto = {
        correlations: correlations,
        is_additive: isAdditive,
        header: {
          name: fundamentalEdgeName
        }
      };
      const newFundamentalEdge: MetricEdgeProto = {
        edge_weight: fundamentalEdgeWeight,
        fundamental_relationship: newFundamentalRelationship,
      };
      setFundamentalEdges((prevForms) => [...prevForms, newFundamentalEdge]);
      setFundamentalEdgeWeight(0);
      setFundamentalEdgeName("");
      setCorrelations([]);
      setIsAdditive(false);
    }
  };
  const handleRemoveFundamentalEdges = (index: number) => {
    setFundamentalEdges(fundamentalEdges.filter((_, i) => i !== index));
  };
  //function to handle select multiple values
  const handleToggle = (value: string) => {
    const currentIndex = selectedColumnValues.indexOf(value);
    let newSelected = [...selectedColumnValues];

    if (currentIndex === -1) {
      newSelected.push(value);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelectedColumnValues(newSelected);
  };
  const submitForm = async () => {
    try {
      const updatedMetrics = metricInfo?.metrics?.map((metric) => {
        const edgesWithRelatedAttributes =
          relatedAttributeEdge &&
          relatedAttributeEdge?.map((edge) => ({
            edge_type: edge?.edge_type,
            edge_weight: edge?.edge_weight || 0.2,
            header: edge?.header!,
            source_metric_id: edge?.source_metric_id
          }));

        const edgesWithBenchmarks = benchmarkEdge?.map((bench) => ({
          edge_type: MetricEdgeType.METRIC_BENCHMARK_EDGE,
          edge_weight: bench?.edge_weight || 0.2,
          header: bench?.header,
          source_metric_id: bench?.source_metric_id
        }));
        const edgesWithCorrelations = fundamentalEdges.map(
          (fundamentalEdge) => ({
            edge_type: MetricEdgeType.METRIC_METRIC_EDGE,
            edge_weight: fundamentalEdge.edge_weight,
            header: fundamentalEdge.header,
            source_metric_id: metricInfo?.metrics[0]?.header?.id
          })
        );

        const edges = [
          ...(edgesWithRelatedAttributes || []),
          ...(edgesWithBenchmarks || []),
          ...(edgesWithCorrelations || []),
        ];

        return {
          ...metric,
          edges,
        };
      });

      // Creating formData object to send to the createMetric API
      const formData: CreateOrUpdateMetrics_Request = {
        model_id: metricInfo?.model_id || "",
        metrics: updatedMetrics as MetricProto[],
        update_edges: true,
      };

      //   API call to create or update the metric
      const responseData = await updateMetric(formData);
      if (responseData)
        showToast("Metric successfully created", "", "success");
    } catch (error) {
      showToast("Error creating metric", "", "error");
    }
  };

  useEffect(() => {
    if (selectedBenchmarkFilterColumn?.column_type === ColumnType.ATTRIBUTE) {
      const getFilterColumnValues = async () => {
        try {
          const response = await getColumnValues(selectedBenchmarkFilterColumn.header?.id!);
            setColumnNames(response);
        } catch (error) {
          console.error("Error fetching column values:", error);
        }
      };
      getFilterColumnValues();
    }
  }, [selectedBenchmarkFilterColumn]);

  const handleAddSelectedBenchmark = async (id: string) => {
    if (id) {
      const benchmark = allBenchmarks?.find((benchmark) => benchmark.id === id);
      if (benchmark) {
        setSelectedBenchmark(benchmark);
      }
    }
  };

  return (
    <CreateMKG
      metricInfo={metricInfo}
      modelInfo={modelInfo}
      allMetrics={allMetrics}
      attributeColumn={attributeColumn}
      measureColumn={measureColumn}
      allBenchmarks={allBenchmarks}
      allKGColumns={allKGColumns}
      selectedBenchmark={selectedBenchmark}
      handleAddSelectedBenchmark={handleAddSelectedBenchmark}
      handleAddRelatedAttributeEdge={handleAddRelatedAttributeEdge}
      handleRemoveRelatedAttribute={handleRemoveRelatedAttribute}
      handleAddBenchmarkFilter={handleAddBenchmarkFilter}
      handleAddBenchmark={handleAddBenchmark}
      handleRemoveBenchmarkFilter={handleRemoveBenchmarkFilter}
      handleRemoveBenchmarkEdge={handleRemoveBenchmarkEdge}
      handleAddCorrelation={handleAddCorrelation}
      handleRemoveCorrelation={handleRemoveCorrelation}
      handleAddFundamentalEdges={handleAddFundamentalEdges}
      handleRemoveFundamentalEdges={handleRemoveFundamentalEdges}
      submitForm={submitForm}
      benchmarkEdge={benchmarkEdge}
      selectedRelatedAttributeColumn={selectedRelatedAttributeColumn}
      setSelectedRelatedAttributeColumn={setSelectedRelatedAttributeColumn}
      relatedAttributeEdge={relatedAttributeEdge}
      relatedAttributeEdgeWeight={relatedAttributeEdgeWeight}
      setRelatedAttributeEdgeWeight={setRelatedAttributeEdgeWeight}
      benchmarkName={benchmarkName}
      setBenchmarkName={setBenchmarkName}
      benchmarkFilterValue={benchmarkFilterValue}
      setBenchmarkFilterValue={setBenchmarkFilterValue}
      benchmarkEdgeWeight={benchmarkEdgeWeight}
      setBenchmarkEdgeWeight={setBenchmarkEdgeWeight}
      fundamentalEdgeName={fundamentalEdgeName}
      setFundamentalEdgeName={setFundamentalEdgeName}
      fundamentalEdgeWeight={fundamentalEdgeWeight}
      setFundamentalEdgeWeight={setFundamentalEdgeWeight}
      selectedBenchmarkFilterColumn={selectedBenchmarkFilterColumn}
      setSelectedBenchmarkFilterColumn={setSelectedBenchmarkFilterColumn}
      selectedType={selectedType}
      setSelectedType={setSelectedType}
      setSelectedHeader={setSelectedHeader}
      correlations={correlations}
      correlationFactor={correlationFactor}
      setCorrelationFactor={setCorrelationFactor}
      benchmarkFilter={benchmarkFilter}
      fundamentalEdges={fundamentalEdges}
      isAdditive={isAdditive}
      setIsAdditive={setIsAdditive}
      isCorrelationMenuOpen={isCorrelationMenuOpen}
      isFilterMenuOpen={isFilterMenuOpen}
      toggleCorrelationMenu={toggleCorrelationMenu}
      toggleFilterMenu={toggleFilterMenu}
      columnNames={columnNames}
      selectedColumnValues={selectedColumnValues}
      handleToggle={handleToggle}

      selectedObservationColumn={selectedObservationColumn}
      selectedFilterColumn={selectedFilterColumn}
      selectedAggregation={selectedAggregation}
      selectedOperator={selectedOperator}
      selectedSorting={selectedSorting}
      inputValue={inputValue}
      selectedFilterColumns={selectedFilterColumns}
      selectedObservationColumns={selectedObservationColumns}

      setColumnNames={setColumnNames}
      setSelectedObservationColumn={setSelectedObservationColumn}
      setSelectedFilterColumn={setSelectedFilterColumn}
      setSelectedColumnValues={setSelectedColumnValues}
      setSelectedAggregation={setSelectedAggregation}
      setSelectedOperator={setSelectedOperator}
      setSelectedSorting={setSelectedSorting}
      setInputValue={setInputValue}
      setSelectedFilterColumns={setSelectedFilterColumns}
      setSelectedObservationColumns={setSelectedObservationColumns}
      targetDataModelHeaders={targetDataModelHeaders}
      targetModelInfo={targetModelInfo}
      selectedTargetModelId={selectedTargetModelId}
      handleTargetModelSelect={handleTargetModelSelect}
      benchmarkMode={benchmarkMode}
      setBenchmarkMode={setBenchmarkMode}
      setDateFilter={setDateFilter}
      setDateFilterCategory={setDateFilterCategory}
      dateFilter={dateFilter}
      dateFilterCategory={dateFilterCategory}
      setSelectedMetricHeader={setSelectedMetricHeader}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
    />
  );
};

export default CreateMKGContainer;
